export enum PollState {
  PROCESSING = 'processing',
  DONE = 'done',
  FAIL = 'fail',
  ERROR = 'error',
}

export interface PollEnvelope<R> {
  state: PollState;
  data?: R;
  error?: string;
  // Backend-only delivery hint on NOT_CACHED failures (the kickoff retracts these instead of replaying); clients never need to read it.
  transient?: boolean;
}

export interface PollKickoffAck {
  pollingKey: string;
  state?: PollState;
  data?: unknown;
  error?: string;
  alreadyInProgress?: boolean;
}

export interface PollTransportParams {
  streamImmediate?: 1;
}

export interface PollableJobConfig<R> {
  kickoff: () => Promise<PollKickoffAck>;
  status: (ack: PollKickoffAck, transportParams: PollTransportParams) => Promise<PollEnvelope<R>>;
  timeoutMs: number;
  eventId?: string;
  onAck?: (ack: PollKickoffAck) => void;
}

export interface ResumePollableJobConfig<R> {
  pollingKey: string;
  status: (ack: PollKickoffAck, transportParams: PollTransportParams) => Promise<PollEnvelope<R>>;
  timeoutMs: number;
  eventId?: string;
}

export class PollFailError extends Error {
  public readonly code: string;
  public readonly data?: unknown;
  constructor(code: string, data?: unknown) {
    super(code);
    this.name = 'PollFailError';
    this.code = code;
    this.data = data;
  }
}

export class PollError extends Error {
  public readonly code?: string;
  constructor(code?: string) {
    super(code ?? 'PollError');
    this.name = 'PollError';
    this.code = code;
  }
}

export class PollTimeoutError extends Error {
  public readonly pollingKey: string;
  constructor(pollingKey: string) {
    super(`pollableJob timed out waiting for key ${pollingKey}`);
    this.name = 'PollTimeoutError';
    this.pollingKey = pollingKey;
  }
}

export const POLL_FALLBACK_PAUSE_MS = 2_000;

export const MALFORMED_DONE_CODE = 'POLL_MALFORMED_DONE';
export const UNKNOWN_STATE_CODE = 'POLL_UNKNOWN_STATE';
